import type { EquipmentDefinition } from "@/types/game-data";

// --------------------------------------------------
// STANDARD EQUIPMENT (available to all elements)
// --------------------------------------------------
export const STANDARD_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "body-armour", name: "Body Armour", cost: 10, access: "standard", category: "armour", rules: "Reduce Lethality by 1. Half-cover bonus when in the open." },
	{
		id: "heavy-body-armour", name: "Heavy Body Armour", cost: 28, access: "standard", category: "armour",
		notes: "Task Force only", rules: "Reduce Lethality by 2. Half-cover bonus when in the open. -1 Movement.",
	},
	{ id: "covert-body-armour", name: "Covert Body Armour", cost: 15, access: "standard", category: "armour", rules: "Reduce Lethality by 1. Concealed — not visible until hit." },
	{ id: "suppressor", name: "Suppressor", cost: 10, access: "standard", category: "build", rules: "Primary weapon becomes Suppressed. Reduces audible detection range." },
	{
		id: "night-vision-mk1", name: "Night Vision (MK1)", cost: 5, access: "standard", category: "optics",
		rules: "MK1: See in darkness up to 12\". Blinded by flash.",
	},
	{
		id: "night-vision-mk2", name: "Night Vision (MK2)", cost: 10, access: "standard", category: "optics",
		rules: "MK2: See in darkness up to 24\". Protected from flash.",
	},
	{ id: "night-fighter", name: "Night Fighter", cost: 5, access: "standard", category: "optics", rules: "Weapon-mounted light. Illuminate targets at 12\". Reveals position." },
	{ id: "cbrn-suit", name: "CBRN Suit", cost: 10, access: "standard", category: "protection", rules: "Immune to chemical/biological effects. -1 Movement." },
	{
		id: "respirator-short", name: "Respirator (Short)", cost: 10, access: "standard", category: "protection",
		rules: "Protects against gas effects for 3 turns.",
	},
	{
		id: "respirator-long", name: "Respirator (Long)", cost: 18, access: "standard", category: "protection",
		rules: "Protects against gas effects for the entire game.",
	},
] as const;

// --------------------------------------------------
// COMMAND EQUIPMENT (Command, Specialist, Mercenary elements)
// --------------------------------------------------
export const COMMAND_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "long-range-comms", name: "Long Range Comms", cost: 20, access: "command", category: "comms", rules: "Allows calling off-table support (artillery, air). 1 per force." },
	{
		id: "combined-forces-comms", name: "Combined Forces Comms", cost: 15, access: "command", category: "comms",
		rules: "Share activations between elements within 12\".",
	},
	{
		id: "forward-observer-comms", name: "Forward Observer Comms", cost: 15, access: "command", category: "comms",
		rules: "Designate targets for off-table assets. Requires LOS.",
	},
	{ id: "laser-designator", name: "Laser Designator", cost: 5, access: "command", category: "comms", rules: "Paint targets for guided munitions. +2 to hit for guided weapons." },
	{ id: "spotter-scope", name: "Spotter Scope", cost: 10, access: "command", category: "comms", rules: "+1 to Awareness rolls when spotting. Extended observation range." },
] as const;

// --------------------------------------------------
// SPECIALIST EQUIPMENT
// --------------------------------------------------
export const SPECIALIST_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "trained-dog", name: "Trained Dog", cost: 30, access: "specialist", category: "k9", rules: "Attack or detect. Move 8\". Assault: L5+/AP-. Can detect hidden/camo units." },
	{
		id: "ballistic-shield-half", name: "Ballistic Shield (Half)", cost: 12, access: "specialist", category: "armour",
		rules: "Frontal half-cover. Sidearm only. -1 Movement.",
	},
	{
		id: "ballistic-shield-full", name: "Ballistic Shield (Full)", cost: 25, access: "specialist", category: "armour",
		rules: "Frontal full-cover. Sidearm only. -2 Movement.",
	},
	{
		id: "breaching-tools-hard", name: "Breaching Tools (Hard)", cost: 8, access: "specialist", category: "breaching",
		rules: "Breach doors/walls mechanically. Action required.",
	},
	{
		id: "breaching-tools-explosive", name: "Breaching Tools (Explosive)", cost: 10, access: "specialist", category: "breaching",
		rules: "Breach doors/walls with explosives. Immediate + Stun effect.",
	},
	{
		id: "breaching-tools-electronic", name: "Breaching Tools (Electronic)", cost: 15, access: "specialist", category: "breaching",
		rules: "Bypass electronic locks silently. Action required.",
	},
	{ id: "lock-pick", name: "Lock Pick", cost: 4, access: "specialist", category: "breaching", rules: "Pick standard locks. Action required. Silent." },
	{ id: "ghillie-suit", name: "Ghillie Suit", cost: 30, access: "specialist", category: "protection", rules: "Hidden when stationary unless within 8\". -1 Movement. No Body Armour." },
] as const;

// --------------------------------------------------
// TIER 1 AND 2 EQUIPMENT (Task Force T1/T2 only)
// --------------------------------------------------
export const TIER1AND2_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "augmented-reality", name: "Augmented Reality", cost: 15, access: "tier1and2", category: "optics", rules: "Integrated HUD with tactical data. +1 Awareness for target acquisition." },
	{
		id: "night-vision-mk3", name: "Night Vision (MK3)", cost: 15, access: "tier1and2", category: "optics",
		rules: "MK3: Full vision in darkness. Immune to flash effects.",
	},
	{
		id: "hyper-aware-operator-suite", name: "Hyper-Aware Operator Suite", cost: 40, access: "tier1and2", category: "optics",
		rules: "Thermal + NV MK3 + Augmented Reality combined. Best-in-class optics.",
	},
	{ id: "hybrid-goggles", name: "Hybrid Goggles", cost: 20, access: "tier1and2", category: "optics", rules: "Combines NV MK2 and thermal capability." },
	{ id: "thermal-optic", name: "Thermal Optic", cost: 32, access: "tier1and2", category: "optics", rules: "See through smoke, detect hidden units. Ignores concealment." },
	{ id: "range-finder", name: "Range Finder", cost: 15, access: "tier1and2", category: "build", rules: "Negate range penalties for one weapon per turn. +1 Ballistics at Extreme." },
	{ id: "exoskeleton", name: "Exoskeleton", cost: 30, access: "tier1and2", category: "protection", rules: "+1 Movement. Negate Heavy Body Armour penalty. Can carry Heavy weapons without penalty." },
	{ id: "thermal-camo", name: "Thermal Camo", cost: 40, access: "tier1and2", category: "protection", rules: "Invisible to thermal optics. Cannot be detected by thermal at any range." },
	{
		id: "gunfire-detector", name: "Gunfire Detector", cost: 10, access: "tier1and2", category: "comms",
		rules: "Automatically detect direction of incoming fire. +1 Awareness when shot at.",
	},
	{ id: "hardened-comms", name: "Hardened Comms", cost: 10, access: "tier1and2", category: "comms", rules: "Immune to comms jamming. Maintain command range regardless of interference." },
	{ id: "ied-jammer", name: "IED Jammer", cost: 10, access: "tier1and2", category: "ordnance", rules: "Prevent remote IED detonation within 6\" radius." },
	{
		id: "cqb-rifle-build", name: "CQB Rifle Build", cost: 10, access: "tier1and2", category: "build",
		rules: "Weapon-mounted light + red dot. +1 modifier at CQB range.",
	},
	{
		id: "mid-rifle-build", name: "Mid Rifle Build", cost: 10, access: "tier1and2", category: "build",
		rules: "LPVO optic. +1 modifier at Engagement range.",
	},
	{
		id: "long-rifle-build", name: "Long Rifle Build", cost: 10, access: "tier1and2", category: "build",
		rules: "High-power optic. +1 modifier at Extreme range.",
	},
	{
		id: "k9-advanced-command", name: "K9 Advanced Command", cost: 10, access: "tier1and2", category: "k9",
		rules: "Upgrade Trained Dog. Dog can relay commands; operator gets +1 to dog actions.",
	},
] as const;

// --------------------------------------------------
// MILITIA-SPECIFIC SPECIALIST EQUIPMENT
// --------------------------------------------------
export const MILITIA_SPECIALIST_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "narcotics", name: "Narcotics", cost: 5, access: "militia-specialist", category: "ordnance", rules: "Ignore first Stress marker. One use." },
	{ id: "ied-switch", name: "IED Switch", cost: 12, access: "militia-specialist", category: "ordnance", rules: "Detonate pre-placed IED as an action. AoE 5." },
	{ id: "bomb-vest", name: "Bomb Vest", cost: 20, access: "militia-specialist", category: "ordnance", rules: "AoE 5 upon death or triggered. L4+/AP2." },
] as const;

// --------------------------------------------------
// RUSSIAN SSO EQUIPMENT
// --------------------------------------------------
export const RUSSIAN_SSO_EQUIPMENT: readonly EquipmentDefinition[] = [
	{
		id: "russian-night-vision-mk1", name: "Night Vision MK1", cost: 15, access: "russian-sso", category: "optics",
		notes: "Russian SSO pricing", rules: "MK1: See in darkness up to 12\". Blinded by flash.",
	},
	{
		id: "hardened-comms-expansion", name: "Hardened Communications", cost: 10, access: "russian-sso", category: "comms",
		rules: "Immune to comms jamming.",
	},
	{
		id: "forward-observation-comms", name: "Forward Observation Communications", cost: 15, access: "russian-sso", category: "comms",
		rules: "Designate targets for off-table assets. Requires LOS.",
	},
	{
		id: "combined-forces-comms-expansion", name: "Combined Forces Communications", cost: 15, access: "russian-sso", category: "comms",
		rules: "Share activations between elements within 12\".",
	},
] as const;

// --------------------------------------------------
// RUSSIAN BREACHER EQUIPMENT
// --------------------------------------------------
export const RUSSIAN_BREACHER_EQUIPMENT: readonly EquipmentDefinition[] = [
	{
		id: "frag-suit", name: "Frag Suit", cost: 0, access: "russian-breacher", category: "armour",
		notes: "Included with Heavy Breacher. Reduces Lethality by 1, -2 vs Frag. Movement -1.",
		rules: "Reduce Lethality by 1 (-2 vs Frag). -1 Movement.",
	},
] as const;

// --------------------------------------------------
// LAW ENFORCEMENT EQUIPMENT
// --------------------------------------------------
export const LAW_ENFORCEMENT_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "le-body-armour", name: "Body Armour", cost: 10, access: "law-enforcement", category: "armour", rules: "Reduce Lethality by 1. Half-cover bonus when in the open." },
	{ id: "le-covert-body-armour", name: "Covert Body Armour", cost: 15, access: "law-enforcement", category: "armour", rules: "Reduce Lethality by 1. Concealed — not visible until hit." },
	{ id: "le-suppressor", name: "Suppressor", cost: 10, access: "law-enforcement", category: "build", rules: "Primary weapon becomes Suppressed." },
	{ id: "le-cqb-build", name: "CQB Build", cost: 10, access: "law-enforcement", category: "build", rules: "+1 modifier at CQB range." },
	{ id: "le-mid-range-build", name: "Mid Range Build", cost: 10, access: "law-enforcement", category: "build", rules: "+1 modifier at Engagement range." },
	{ id: "le-long-range-build", name: "Long Range Build", cost: 10, access: "law-enforcement", category: "build", rules: "+1 modifier at Extreme range." },
	{ id: "le-respirator-short", name: "Respirator (Short)", cost: 10, access: "law-enforcement", category: "protection", rules: "Protects against gas for 3 turns." },
	{ id: "le-respirator-long", name: "Respirator (Long)", cost: 18, access: "law-enforcement", category: "protection", rules: "Protects against gas for the entire game." },
	{ id: "le-ied-jammer", name: "IED Jammer", cost: 10, access: "law-enforcement", category: "ordnance", rules: "Prevent remote IED detonation within 6\"." },
	{ id: "le-cbrn-suit", name: "CBRN Suit", cost: 10, access: "law-enforcement", category: "protection", rules: "Immune to chemical/biological effects. -1 Movement." },
	{ id: "le-ghillie-suit", name: "Ghillie Suit", cost: 30, access: "law-enforcement", category: "protection", rules: "Hidden when stationary unless within 8\". -1 Movement." },
	{ id: "le-night-vision-mk1", name: "Night Vision MK1", cost: 5, access: "law-enforcement", category: "optics", rules: "See in darkness up to 12\"." },
	{ id: "le-night-vision-mk2", name: "Night Vision MK2", cost: 10, access: "law-enforcement", category: "optics", rules: "See in darkness up to 24\". Protected from flash." },
	{ id: "le-thermal-optic", name: "Thermal Optic", cost: 32, access: "law-enforcement", category: "optics", rules: "See through smoke, detect hidden units." },
	{ id: "le-night-fighter", name: "Night Fighter", cost: 5, access: "law-enforcement", category: "optics", rules: "Weapon-mounted light. Illuminate targets at 12\"." },
] as const;

// --------------------------------------------------
// LAW ENFORCEMENT SPECIALIST EQUIPMENT
// --------------------------------------------------
export const LAW_ENFORCEMENT_SPECIALIST_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "le-ballistic-full-shield", name: "Ballistic Full Shield", cost: 25, access: "law-enforcement-specialist", category: "armour", rules: "Frontal full-cover. Sidearm only. -2 Movement." },
	{ id: "le-ballistic-half-shield", name: "Ballistic Half Shield", cost: 12, access: "law-enforcement-specialist", category: "armour", rules: "Frontal half-cover. Sidearm only. -1 Movement." },
	{ id: "le-trained-dog", name: "Trained Dog", cost: 30, access: "law-enforcement-specialist", category: "k9", rules: "Attack or detect. Move 8\". Can detect hidden/camo units." },
	{ id: "le-heavy-body-armour", name: "Heavy Body Armour", cost: 28, access: "law-enforcement-specialist", category: "armour", rules: "Reduce Lethality by 2. Half-cover in open. -1 Movement." },
	{ id: "le-long-range-comms", name: "Long Range Comms", cost: 20, access: "law-enforcement-specialist", category: "comms", rules: "Allows calling off-table support. 1 per force." },
	{ id: "le-laser-designator", name: "Laser Designator", cost: 5, access: "law-enforcement-specialist", category: "comms", rules: "+2 to hit for guided weapons." },
	{ id: "le-combined-forces-comms", name: "Combined Forces Communications", cost: 15, access: "law-enforcement-specialist", category: "comms", rules: "Share activations between elements within 12\"." },
	{ id: "le-breaching-hard", name: "Breaching Tools (Hard Entry)", cost: 8, access: "law-enforcement-specialist", category: "breaching", rules: "Breach doors/walls mechanically." },
	{ id: "le-breaching-explosive", name: "Explosive Entry Tools", cost: 10, access: "law-enforcement-specialist", category: "breaching", rules: "Breach doors/walls with explosives. Stun effect." },
	{ id: "le-breaching-electronic", name: "Electronic Entry Tools", cost: 15, access: "law-enforcement-specialist", category: "breaching", rules: "Bypass electronic locks silently." },
] as const;

// --------------------------------------------------
// CRIMINAL BASIC EQUIPMENT
// --------------------------------------------------
export const CRIMINAL_BASIC_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "criminal-body-armour", name: "Body Armour", cost: 20, access: "criminal-basic", category: "armour", notes: "Criminal Element pricing", rules: "Reduce Lethality by 1. Half-cover in open." },
	{ id: "criminal-long-range-comms", name: "Long Range Comms", cost: 20, access: "criminal-basic", category: "comms", rules: "Allows calling off-table support." },
	{ id: "untrained-dog", name: "Untrained Dog", cost: 30, access: "criminal-basic", category: "k9", rules: "Move 8\". Assault: L6+/AP-. Cannot be commanded precisely." },
	{ id: "criminal-narcotics", name: "Narcotics", cost: 5, access: "criminal-basic", category: "ordnance", rules: "Ignore first Stress marker. One use." },
	{ id: "criminal-suppressor", name: "Suppressor", cost: 30, access: "criminal-basic", category: "build", notes: "Criminal Element pricing", rules: "Primary weapon becomes Suppressed." },
] as const;

// --------------------------------------------------
// CRIMINAL ORGANISED EQUIPMENT (Organised Criminal Element)
// --------------------------------------------------
export const CRIMINAL_ORGANISED_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "oc-body-armour", name: "Body Armour", cost: 10, access: "criminal-organised", category: "armour", rules: "Reduce Lethality by 1. Half-cover in open." },
	{ id: "oc-covert-body-armour", name: "Covert Body Armour", cost: 15, access: "criminal-organised", category: "armour", rules: "Reduce Lethality by 1. Concealed." },
	{ id: "oc-heavy-body-armour", name: "Heavy Body Armour", cost: 28, access: "criminal-organised", category: "armour", rules: "Reduce Lethality by 2. Half-cover in open. -1 Movement." },
	{ id: "oc-breaching-hard", name: "Breaching Tools (Hard Entry)", cost: 8, access: "criminal-organised", category: "breaching", rules: "Breach doors/walls mechanically." },
	{ id: "oc-lock-pick", name: "Lock Pick", cost: 4, access: "criminal-organised", category: "breaching", rules: "Pick standard locks. Silent." },
	{ id: "oc-respirator-short", name: "Respirator (Short)", cost: 10, access: "criminal-organised", category: "protection", rules: "Protects against gas for 3 turns." },
	{ id: "oc-respirator-long", name: "Respirator (Long)", cost: 18, access: "criminal-organised", category: "protection", rules: "Protects against gas for the entire game." },
	{ id: "oc-suppressor", name: "Suppressor", cost: 30, access: "criminal-organised", category: "build", notes: "Criminal Element pricing", rules: "Primary weapon becomes Suppressed." },
	{ id: "oc-night-vision-mk1", name: "Night Vision MK1", cost: 20, access: "criminal-organised", category: "optics", notes: "Criminal Element pricing", rules: "See in darkness up to 12\"." },
	{ id: "oc-night-vision-mk2", name: "Night Vision MK2", cost: 28, access: "criminal-organised", category: "optics", notes: "Criminal Element pricing", rules: "See in darkness up to 24\"." },
	{ id: "oc-cbrn-suit", name: "CBRN Suit", cost: 10, access: "criminal-organised", category: "protection", rules: "Immune to chemical/biological effects. -1 Movement." },
	{ id: "oc-untrained-dog", name: "Untrained Dog", cost: 30, access: "criminal-organised", category: "k9", rules: "Move 8\". Assault: L6+/AP-. Cannot be commanded precisely." },
	{ id: "oc-bomb-vest", name: "Bomb Vest", cost: 25, access: "criminal-organised", category: "ordnance", notes: "Criminal Element pricing", rules: "AoE 5 upon death or triggered. L4+/AP2." },
	{ id: "oc-narcotics", name: "Narcotics", cost: 5, access: "criminal-organised", category: "ordnance", rules: "Ignore first Stress marker. One use." },
] as const;

// --------------------------------------------------
// CRIMINAL GANG EQUIPMENT
// --------------------------------------------------
export const CRIMINAL_GANG_EQUIPMENT: readonly EquipmentDefinition[] = [
	{ id: "cg-body-armour", name: "Body Armour", cost: 20, access: "criminal-basic", category: "armour", notes: "Criminal Gang pricing", rules: "Reduce Lethality by 1. Half-cover in open." },
	{ id: "cg-breaching-hard", name: "Breaching Tools (Hard Entry)", cost: 8, access: "criminal-basic", category: "breaching", rules: "Breach doors/walls mechanically." },
	{ id: "cg-long-range-comms", name: "Long Range Comms (Sergeant Only)", cost: 20, access: "criminal-basic", category: "comms", notes: "Sergeant only", rules: "Allows calling off-table support." },
	{ id: "cg-lock-pick", name: "Lock Pick", cost: 4, access: "criminal-basic", category: "breaching", rules: "Pick standard locks. Silent." },
	{ id: "cg-untrained-dog", name: "Untrained Dog", cost: 30, access: "criminal-basic", category: "k9", rules: "Move 8\". Assault: L6+/AP-." },
	{ id: "cg-suppressor", name: "Suppressor", cost: 30, access: "criminal-basic", category: "build", notes: "Criminal Gang pricing", rules: "Primary weapon becomes Suppressed." },
	{ id: "cg-narcotics", name: "Narcotics", cost: 5, access: "criminal-basic", category: "ordnance", rules: "Ignore first Stress marker. One use." },
] as const;

// --------------------------------------------------
// ALL EQUIPMENT combined
// --------------------------------------------------
export const ALL_EQUIPMENT: readonly EquipmentDefinition[] = [
	...STANDARD_EQUIPMENT,
	...COMMAND_EQUIPMENT,
	...SPECIALIST_EQUIPMENT,
	...TIER1AND2_EQUIPMENT,
	...MILITIA_SPECIALIST_EQUIPMENT,
	...RUSSIAN_SSO_EQUIPMENT,
	...RUSSIAN_BREACHER_EQUIPMENT,
	...LAW_ENFORCEMENT_EQUIPMENT,
	...LAW_ENFORCEMENT_SPECIALIST_EQUIPMENT,
	...CRIMINAL_BASIC_EQUIPMENT,
	...CRIMINAL_ORGANISED_EQUIPMENT,
	...CRIMINAL_GANG_EQUIPMENT,
] as const;

/** Lookup equipment by ID */
export function getEquipmentById(id: string): EquipmentDefinition | undefined {
	return ALL_EQUIPMENT.find((e) => e.id === id);
}

/** Get equipment filtered by access levels */
export function getEquipmentByAccess(
	accessLevels: readonly string[],
): readonly EquipmentDefinition[] {
	return ALL_EQUIPMENT.filter((e) => accessLevels.includes(e.access));
}
